<template>
  <div class="glass-card rounded-lg p-6">
    <div class="flex flex-col lg:flex-row lg:items-center justify-between mb-6 space-y-4 lg:space-y-0">
      <h2 class="text-xl font-semibold text-white">Gäste Liste</h2>
      <div class="flex flex-col sm:flex-row items-start sm:items-center space-y-3 sm:space-y-0 sm:space-x-3">
        <div class="relative w-full sm:w-auto">
          <input 
            v-model="searchQuery"
            type="text" 
            placeholder="Suche nach Name oder Firma..."
            class="glass-morphism text-white placeholder-gray-400 px-4 py-2 pr-10 rounded-lg focus:outline-none focus:ring-2 focus:ring-fvv-orange w-full sm:w-64"
          >
          <i class="fas fa-search absolute right-3 top-3 text-gray-400"></i>
        </div>
        <select 
          v-model="statusFilter"
          class="glass-morphism text-white px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-fvv-orange w-full sm:w-auto"
        >
          <option value="">Alle Status</option>
          <option value="true">Eingecheckt</option>
          <option value="false">Ausstehend</option>
        </select>
        <div class="flex glass-morphism rounded-lg p-1 w-full sm:w-auto justify-center">
          <button 
            @click="viewMode = 'table'"
            :class="viewMode === 'table' ? 'btn-primary text-white' : 'text-gray-300 hover:text-white'"
            class="px-3 py-1 rounded transition-all duration-300"
          >
            <i class="fas fa-list"></i>
          </button>
          <button 
            @click="viewMode = 'tiles'"
            :class="viewMode === 'tiles' ? 'btn-primary text-white' : 'text-gray-300 hover:text-white'"
            class="px-3 py-1 rounded transition-all duration-300"
          >
            <i class="fas fa-th-large"></i>
          </button>
          <button 
            @click="$emit('export-csv')"
            class="text-white hover:text-fvv-orange px-3 py-1 rounded transition-all duration-300"
            title="Export CSV"
          >
            <i class="fas fa-download"></i>
          </button>
          <button 
            @click="$emit('refresh')"
            :class="[
              'text-white hover:text-fvv-orange px-3 py-1 rounded transition-all duration-300',
              isRefreshing ? 'opacity-50 cursor-not-allowed animate-spin' : ''
            ]"
            :disabled="isRefreshing"
            title="Refresh"
          >
            <i :class="isRefreshing ? 'fas fa-spinner' : 'fas fa-sync-alt'"></i>
          </button>
        </div>
      </div>
    </div>
    
    <!-- Table View -->
    <div v-if="viewMode === 'table'" class="overflow-x-auto">
      <table class="w-full">
        <thead>
          <tr class="border-b border-white/10">
            <th class="text-left py-3 px-4 text-gray-400 font-medium">First Name</th>
            <th class="text-left py-3 px-4 text-gray-400 font-medium">Last Name</th>
            <th class="text-left py-3 px-4 text-gray-400 font-medium">Firma</th>
            <th class="text-left py-3 px-4 text-gray-400 font-medium">Status</th>
            <th class="text-left py-3 px-4 text-gray-400 font-medium">Aktion</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="guest in filteredGuests" :key="guest.uniqueId" class="border-b border-white/5 hover:bg-white/5 transition-colors">
            <td class="py-3 px-4 text-white">
              {{ guest.firstName }} 
            </td>
            <td class="py-3 px-4 text-white">
              {{ guest.lastName }} 
            </td>
            <td class="py-3 px-4 text-gray-300">{{ guest.company }}</td>
            <td class="py-3 px-4">
              <span :class="guest.checkedIn ? 'bg-green-500/20 text-green-400 border-green-500/30' : 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30'" 
                    class="status-badge px-3 py-1 rounded-full text-sm border">
                {{ guest.checkedIn ? 'Eingecheckt' : 'Ausstehend' }}
              </span>
            </td>
            <td class="py-3 px-4">
              <div class="flex items-center space-x-2">
                <button 
                  @click="$emit('check-in', guest)"
                  :disabled="guest.checkedIn"
                  :class="guest.checkedIn ? 'opacity-40 cursor-not-allowed bg-gray-600/30 text-white px-3 py-1.5 rounded-lg text-sm' : 'btn-primary text-white px-3 py-1.5 rounded-lg text-sm'"
                  class="transition-all duration-300"
                  title="Check-in"
                >
                  <i class="fas fa-check mr-1"></i>
                  {{ guest.checkedIn ? 'OK' : 'Check-in' }}
                </button>
                <button 
                  v-if="guest.checkedIn"
                  @click="$emit('reset-guest', guest)"
                  class="text-yellow-400 hover:text-yellow-300 bg-yellow-500/20 hover:bg-yellow-500/30 px-3 py-1.5 rounded-lg text-sm transition-all duration-300"
                  title="Check-in zurücksetzen"
                >
                  <i class="fas fa-undo mr-1"></i>
                  Reset
                </button>
                <button 
                  @click="$emit('delete-guest', guest)"
                  class="text-red-400 hover:text-red-300 bg-red-500/20 hover:bg-red-500/30 px-3 py-1.5 rounded-lg text-sm transition-all duration-300"
                  title="Gast löschen"
                >
                  <i class="fas fa-trash"></i>
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Tile View -->
    <div v-if="viewMode === 'tiles'" class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
      <div v-for="guest in filteredGuests" :key="guest.uniqueId" class="glass-card rounded-lg p-4 hover-lift">
        <div class="flex items-start justify-between mb-3">
          <div>
            <h3 class="text-white font-semibold">
              {{ guest.title }} {{ guest.firstName }} {{ guest.lastName }}
            </h3>
            <p class="text-gray-400 text-sm">{{ guest.company }}</p>
          </div>
          <span :class="guest.checkedIn ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'" 
                class="status-badge px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap flex-shrink-0 inline-flex items-center">
            <i :class="guest.checkedIn ? 'fas fa-check' : 'fas fa-clock'" class="mr-1"></i>
            {{ guest.checkedIn ? 'Present' : 'Absent' }}
          </span>
        </div>
        
        <div class="space-y-2 mb-4">
          <div class="flex items-center text-sm text-gray-400">
            <i class="fas fa-envelope mr-2 w-4"></i>
            {{ getEmailFromJson(guest.jsonData) }}
          </div>
          <div class="flex items-center text-sm text-gray-400">
            <i class="fas fa-fingerprint mr-2 w-4"></i>
            <span class="font-mono">{{ guest.uniqueId.substring(0, 20) }}...</span>
          </div>
        </div>
        
        <div class="flex space-x-2">
          <button @click="$emit('check-in', guest)"
                  :disabled="guest.checkedIn"
                  :class="guest.checkedIn ? 'bg-gray-600/30 cursor-not-allowed' : 'btn-primary'"
                  class="flex-1 px-3 py-2 rounded-lg text-sm text-white transition-all">
            <i class="fas fa-sign-in-alt mr-1"></i>
            Check-in
          </button>
          <button v-if="guest.checkedIn"
                  @click="$emit('reset-guest', guest)"
                  class="px-3 py-2 rounded-lg text-sm text-yellow-400 bg-yellow-500/20 hover:bg-yellow-500/30 transition-all"
                  title="Check-in zurücksetzen">
            <i class="fas fa-undo"></i>
          </button>
          <button @click="$emit('delete-guest', guest)"
                  class="px-3 py-2 rounded-lg text-sm text-red-400 bg-red-500/20 hover:bg-red-500/30 transition-all"
                  title="Gast löschen">
            <i class="fas fa-trash"></i>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'GuestList',
  props: {
    guests: {
      type: Array,
      required: true
    },
    isRefreshing: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      searchQuery: '',
      statusFilter: '',
      viewMode: 'tiles' // Default to tiles for mobile
    }
  },
  computed: {
    filteredGuests() {
      return this.guests.filter(guest => {
        const matchesSearch = !this.searchQuery || 
          `${guest.firstName} ${guest.lastName} ${guest.company}`.toLowerCase().includes(this.searchQuery.toLowerCase());
        const matchesStatus = this.statusFilter === '' || guest.checkedIn.toString() === this.statusFilter;
        return matchesSearch && matchesStatus;
      });
    }
  },
  methods: {
    getEmailFromJson(jsonData) {
      try {
        const data = JSON.parse(jsonData);
        return data['main-mail'] || data['email-1'] || 'N/A';
      } catch {
        return 'N/A';
      }
    }
  },
  emits: ['check-in', 'reset-guest', 'delete-guest', 'export-csv', 'refresh']
}
</script>
